/**
 * Inventory Transactions Component
 * Main container for viewing transaction history across all inventory types.
 * Products use dedicated hook + list + dialog; all others use the generic path.
 */

import React, { useState, useMemo, useEffect } from 'react';
import {
  Box, Typography, Chip, IconButton, Tooltip, Select, MenuItem,
  useTheme, alpha
} from '@mui/material';
import { Refresh as RefreshIcon } from '@mui/icons-material';
import { useLocations } from './shared/useInventoryData';
import { getInventoryConfig } from './shared/inventoryConfig';
import useTransactions from './transactions-components/useTransactions';
import useProductTransactions from './transactions-components/useProductTransactions';
import Filters from './transactions-components/Filters';
import TransactionsList from './transactions-components/TransactionsList';
import ProductTransactionsList from './transactions-components/ProductTransactionsList';
import ViewTransactionDialogue from './transactions-components/ViewTransactionDialogue';
import ProductViewTransactionDialogue from './transactions-components/ProductViewTransactionDialogue';
import EmptyState from './transactions-components/EmptyState';
import AllInventoryTransactions from './all-inventory/AllInventoryTransactions';

const TRANSACTION_INVENTORY_TYPES = [
  { value: 'all',       label: 'All Inventory' },
  { value: 'products',  label: 'Products'  },
  { value: 'chemicals', label: 'Chemicals' },
  { value: 'caps',      label: 'Caps'      },
  { value: 'bottles',   label: 'Bottles'   },
  { value: 'labels',    label: 'Labels'    },
  { value: 'supplies',  label: 'Supplies'  }
];

const VALID_TYPES = TRANSACTION_INVENTORY_TYPES.map(t => t.value);

export default function InventoryTransactions({ companyId, inventoryType: initialType, onTypeChange }) {
  const theme = useTheme();

  const getValidType = (type) => VALID_TYPES.includes(type) ? type : 'all';
  const [inventoryType, setInventoryType] = useState(getValidType(initialType));

  const isProduct = inventoryType === 'products';

  // Sync when parent changes the type
  useEffect(() => {
    if (initialType && VALID_TYPES.includes(initialType)) {
      setInventoryType(initialType);
    }
  }, [initialType]);

  const config      = inventoryType !== 'all' ? getInventoryConfig(inventoryType) : null;
  const { locations } = useLocations(companyId);

  // Both hooks are always called (React rules); only the active one gets a real companyId.
  const generalHook = useTransactions(inventoryType, isProduct ? null : companyId);
  const productHook = useProductTransactions(isProduct ? companyId : null);

  const { transactions, loading, refresh, timeFormat, updateTimeFormat } =
    isProduct ? productHook : generalHook;

  // Filter states
  const [searchQuery,      setSearchQuery]      = useState('');
  const [selectedType,     setSelectedType]      = useState('');
  const [selectedLocation, setSelectedLocation]  = useState('');
  const [fromDate,         setFromDate]          = useState('');
  const [toDate,           setToDate]            = useState('');

  // Pagination
  const [page,        setPage]        = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(12);

  // View dialog
  const [viewDialogOpen,      setViewDialogOpen]      = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);

  // Filter
  const filteredTransactions = useMemo(() => {
    return (transactions || []).filter(tx => {
      if (searchQuery) {
        const q        = searchQuery.toLowerCase();
        const itemName = (isProduct ? tx.products?.name        : tx[config.tableName]?.name)?.toLowerCase() || '';
        const itemSku  = (isProduct ? tx.products?.sku         : tx[config.tableName]?.sku)?.toLowerCase()  || '';
        const variant  = isProduct ? (tx.variants?.name?.toLowerCase() || '') : '';
        const notes    = tx.notes?.toLowerCase()            || '';
        const ref      = tx.reference_number?.toLowerCase() || '';
        const invoice  = tx.invoice_number?.toLowerCase()   || '';
        const po       = tx.po_number?.toLowerCase()        || '';
        if (![itemName, itemSku, variant, notes, ref, invoice, po].some(f => f.includes(q))) return false;
      }
      if (selectedType && tx.transaction_type !== selectedType) return false;
      if (selectedLocation) {
        const matches =
          tx.location_id      === selectedLocation ||
          tx.from_location_id === selectedLocation ||
          tx.to_location_id   === selectedLocation;
        if (!matches) return false;
      }
      if (fromDate) {
        const txDate = new Date(tx.transaction_date || tx.created_at).toISOString().split('T')[0];
        if (txDate < fromDate) return false;
      }
      if (toDate) {
        const txDate = new Date(tx.transaction_date || tx.created_at).toISOString().split('T')[0];
        if (txDate > toDate) return false;
      }
      return true;
    });
  }, [transactions, searchQuery, selectedType, selectedLocation, fromDate, toDate, config, isProduct]);

  React.useEffect(() => { setPage(0); }, [filteredTransactions.length]);

  const handleTypeChange = (newType) => {
    setInventoryType(newType);
    if (onTypeChange) onTypeChange(newType);
    setSearchQuery('');
    setSelectedType('');
    setSelectedLocation('');
    setFromDate('');
    setToDate('');
    setPage(0);
  };

  const paginatedTransactions = useMemo(() => {
    const start = page * rowsPerPage;
    return filteredTransactions.slice(start, start + rowsPerPage);
  }, [filteredTransactions, page, rowsPerPage]);

  const handleViewTransaction = (tx) => {
    setSelectedTransaction(tx);
    setViewDialogOpen(true);
  };

  const clearFilters = () => {
    setSearchQuery(''); setSelectedType(''); setSelectedLocation(''); setFromDate(''); setToDate('');
  };

  const typeSelector = (
    <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
      <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.9rem' }}>
        Inventory Type:
      </Typography>
      <Select
        value={inventoryType}
        onChange={(e) => handleTypeChange(e.target.value)}
        size="small"
        sx={{
          minWidth: 200, borderRadius: 3, bgcolor: 'white',
          '& .MuiOutlinedInput-notchedOutline': { border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}` },
          '&:hover .MuiOutlinedInput-notchedOutline': { border: `1px solid ${theme.palette.primary.main}` },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': { border: `1px solid ${theme.palette.primary.main}` }
        }}
      >
        {TRANSACTION_INVENTORY_TYPES.map(opt => (
          <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
        ))}
      </Select>
    </Box>
  );

  if (inventoryType === 'all') {
    return (
      <Box sx={{ p: 4 }}>
        {typeSelector}
        <AllInventoryTransactions companyId={companyId} />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 4 }}>
      {typeSelector}

      {/* Header */}
      <Box sx={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
        mb: 3, p: 3, borderRadius: 4,
        border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
        background: 'white',
        boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.08)}`,
        position: 'relative', overflow: 'hidden',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: `0 16px 48px ${alpha(theme.palette.primary.main, 0.15)}`,
          border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`
        },
        '&::after': {
          content: '""', position: 'absolute', top: 0, right: 0,
          width: '200px', height: '200px',
          background: `radial-gradient(circle, ${alpha(theme.palette.primary.main, 0.08)} 0%, transparent 70%)`,
          borderRadius: '50%', transform: 'translate(50%, -50%)', zIndex: 0
        }
      }}>
        <Box sx={{ position: 'relative', zIndex: 1, flex: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <Box sx={{
              width: 12, height: 12, borderRadius: '50%', bgcolor: 'success.main',
              animation: 'pulse 2s infinite',
              '@keyframes pulse': { '0%': { opacity: 1 }, '50%': { opacity: 0.5 }, '100%': { opacity: 1 } }
            }} />
            <Typography variant="caption" sx={{ color: 'success.main', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '1px', fontSize: '0.75rem' }}>
              Transaction History
            </Typography>
          </Box>
          <Typography variant="h4" sx={{ fontWeight: 500, color: 'text.primary', fontSize: '1.8rem', lineHeight: 1.2, mb: 1 }}>
            {config.displayName} Transactions
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500, fontSize: '0.9rem', mb: 2.5, maxWidth: '400px' }}>
            View addition, transfer, usage, and adjustment history
          </Typography>
          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
            <Chip
              label={`${filteredTransactions.length} Transactions`}
              size="small"
              sx={{ bgcolor: alpha(theme.palette.info.main, 0.1), color: 'info.main', fontWeight: 500, border: `1px solid ${alpha(theme.palette.info.main, 0.2)}` }}
            />
            <Chip
              label={`${filteredTransactions.filter(t => t.transaction_type === 'inventory_creation').length} Additions`}
              size="small"
              sx={{ bgcolor: alpha(theme.palette.success.main, 0.1), color: 'success.main', fontWeight: 500, border: `1px solid ${alpha(theme.palette.success.main, 0.2)}` }}
            />
            <Chip
              label={`${filteredTransactions.filter(t => t.transaction_type === 'transfer').length} Transfers`}
              size="small"
              sx={{ bgcolor: alpha(theme.palette.primary.main, 0.1), color: 'primary.main', fontWeight: 500, border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}` }}
            />
            <Chip
              label={`${filteredTransactions.filter(t => t.transaction_type === 'usage').length} Usage`}
              size="small"
              sx={{ bgcolor: alpha(theme.palette.warning.main, 0.1), color: 'warning.main', fontWeight: 500, border: `1px solid ${alpha(theme.palette.warning.main, 0.2)}` }}
            />
          </Box>
        </Box>

        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', position: 'relative', zIndex: 1, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          <Select
            value={timeFormat}
            onChange={(e) => updateTimeFormat(e.target.value)}
            size="small"
            sx={{
              minWidth: 110, borderRadius: 2, bgcolor: 'white', fontWeight: 500,
              '& .MuiOutlinedInput-notchedOutline': { border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}` },
              '&:hover .MuiOutlinedInput-notchedOutline': { border: `1px solid ${theme.palette.primary.main}` },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': { border: `1px solid ${theme.palette.primary.main}` }
            }}
          >
            <MenuItem value="12h">12-hour</MenuItem>
            <MenuItem value="24h">24-hour</MenuItem>
          </Select>

          <Tooltip title="Refresh transactions">
            <IconButton
              onClick={refresh}
              sx={{
                bgcolor: alpha(theme.palette.primary.main, 0.08),
                border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                color: 'primary.main',
                '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.15), transform: 'rotate(180deg)', transition: 'all 0.4s ease' }
              }}
            >
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Filters */}
      <Filters
        searchQuery={searchQuery}           onSearchChange={setSearchQuery}
        selectedType={selectedType}         onTypeChange={setSelectedType}
        selectedLocation={selectedLocation} onLocationChange={setSelectedLocation}
        fromDate={fromDate}                 onFromDateChange={setFromDate}
        toDate={toDate}                     onToDateChange={setToDate}
        locations={locations || []}
      />

      {/* List or Empty State */}
      {filteredTransactions.length === 0 && !loading ? (
        <EmptyState
          hasTransactions={(transactions || []).length > 0}
          onClearFilters={clearFilters}
        />
      ) : isProduct ? (
        <ProductTransactionsList
          transactions={paginatedTransactions}
          totalCount={filteredTransactions.length}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={(_, newPage) => setPage(newPage)}
          onRowsPerPageChange={(n) => { setRowsPerPage(n); setPage(0); }}
          timeFormat={timeFormat}
          onViewTransaction={handleViewTransaction}
        />
      ) : (
        <TransactionsList
          transactions={paginatedTransactions}
          totalCount={filteredTransactions.length}
          page={page}
          rowsPerPage={rowsPerPage}
          onPageChange={(_, newPage) => setPage(newPage)}
          onRowsPerPageChange={(n) => { setRowsPerPage(n); setPage(0); }}
          config={config}
          timeFormat={timeFormat}
          loading={loading}
          onViewTransaction={handleViewTransaction}
        />
      )}

      {/* View Dialog */}
      {isProduct ? (
        <ProductViewTransactionDialogue
          open={viewDialogOpen}
          onClose={() => { setViewDialogOpen(false); setSelectedTransaction(null); }}
          transaction={selectedTransaction}
          timeFormat={timeFormat}
        />
      ) : (
        <ViewTransactionDialogue
          open={viewDialogOpen}
          onClose={() => { setViewDialogOpen(false); setSelectedTransaction(null); }}
          transaction={selectedTransaction}
          config={config}
          timeFormat={timeFormat}
        />
      )}
    </Box>
  );
}
